import message from "components/CustomMessage";
import {
  CVSS_MODIFIER_FIELDS,
  NOT_DEFINED_OPTION,
} from "constants/CVSSConstants";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  getStringFromModifiedCVSSObject,
  parseCVSSMetricsStringToFields,
} from "utils/cvssUtil";

import ACButton from "ac-components/ac-button/ACButton";
import ACModalV2 from "ac-components/ac-modal/ACModalV2";

import CVSSFormFields from "./CVSSFormFields";
import "./CVSSModifierPopup.less";

export default function CVSSModifierPopup({
  titlePrefix,
  subtitle,
  showHeader = true,
  onSubmit,
  initialCvSSString,
  inheritedCVSSModifier,
}: {
  titlePrefix: ReactNode;
  subtitle: ReactNode;
  showHeader?: boolean;
  onSubmit: (cvssString: string) => Promise<void>;
  initialCvSSString?: string;
  inheritedCVSSModifier?: string;
}) {
  const fields = CVSS_MODIFIER_FIELDS["3.1"];
  const modalSettings = ACModalV2.useModalSettings();

  const inheritedCvssValues = useMemo(() => {
    if (inheritedCVSSModifier) {
      return parseCVSSMetricsStringToFields(inheritedCVSSModifier);
    }
    return {} as { [key: string]: string };
  }, [inheritedCVSSModifier]);

  const defaultState = useMemo(
    () =>
      fields.reduce(
        (acc, field) => {
          acc[field.name] = NOT_DEFINED_OPTION.value;
          return acc;
        },
        {} as { [key: string]: string },
      ),
    [fields],
  );

  const initialCvssState = useMemo(() => {
    if (initialCvSSString) {
      const parsedFields = parseCVSSMetricsStringToFields(initialCvSSString);
      return { ...defaultState, ...parsedFields };
    }

    return defaultState;
  }, [defaultState, initialCvSSString]);

  const [cvssObject, setCvssObject] = useState(initialCvssState);
  const [loading, setLoading] = useState(false);

  const hasChanges = useMemo(() => {
    return JSON.stringify(initialCvssState) !== JSON.stringify(cvssObject);
  }, [initialCvssState, cvssObject]);

  useEffect(() => {
    modalSettings.updateShowWarningOnClose(hasChanges, {
      promptMessage: "Unsaved Changes, are you sure you want to proceed?",
      okText: "Yes, Don't Save Changes",
      cancelText: "Back",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasChanges]);

  const handleCvssChange = (value: string, fieldName: string) => {
    setCvssObject((prevState) => ({
      ...prevState,
      [fieldName]: value,
    }));
  };

  const handleReset = () => {
    if (inheritedCVSSModifier) {
      // If inherited values exist, reset to those merged with the default
      const inheritedState = { ...defaultState, ...inheritedCvssValues };
      setCvssObject(inheritedState);
    } else {
      setCvssObject(defaultState);
    }
  };

  const handleSave = useCallback(async () => {
    const finalCvssObject = cvssObject;

    const objectValues = Object.values(cvssObject);
    // Check if every value in the state is the "Not Defined" value.
    const allValuesAreNotDefined =
      objectValues.length > 0 &&
      objectValues.every((val) => val === NOT_DEFINED_OPTION.value);

    const cvssString = allValuesAreNotDefined
      ? ""
      : getStringFromModifiedCVSSObject(finalCvssObject);
    try {
      setLoading(true);
      await onSubmit(cvssString);
      message.success("Successfully modified CVSS vector");
      modalSettings.updateShowWarningOnClose(false);
      modalSettings.close();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [cvssObject, onSubmit, modalSettings]);

  return (
    <>
      <ACModalV2.Title>{titlePrefix} CVSS Modifiers</ACModalV2.Title>
      <CVSSFormFields
        subtitle={subtitle}
        handleCvssChange={handleCvssChange}
        inheritedCvssValues={inheritedCvssValues}
        showHeader={showHeader}
        fields={fields}
        cvssObject={cvssObject}
      />
      <ACModalV2.Footer className="d-flex justify-content-end">
        <ACButton onClick={handleReset} type="ghost">
          Clear
        </ACButton>
        <ACButton loading={loading} onClick={handleSave} disabled={!hasChanges}>
          Save
        </ACButton>
      </ACModalV2.Footer>
    </>
  );
}
